import { ReactNode } from "react";

import {
    AbfLoanExpanded,
    isManageLeverageEligable,
    canLenderSellLoan,
    getLoanName,
    getZcLedger,
    getZcLoanCollateral,
    isLedgerInRefinanceGrace,
    isLoanActive,
    isLoanFullyRepaid,
    isLoopLoan,
    isRaydiumMetadata,
    isWhirlpoolMetadata,
    LoopPositionExpanded,
    isLoanDefaulted
} from "@bridgesplit/abf-react";
import {
    Button,
    ButtonProps,
    Column,
    MenuItemProps,
    MenuItems,
    PopoverWrapper,
    Row,
    SPACING,
    Text,
    usePopover,
    Icon,
    DialogWrapper,
    LOOP_UNWIND_MAX_WIDTH
} from "@bridgesplit/ui";
import { abbreviateString } from "@bridgesplit/utils";
import {
    ArrowOutwardOutlined,
    ExpandMore,
    FeedOutlined,
    HealthAndSafetyOutlined,
    MoreTimeOutlined,
    PaymentOutlined,
    CheckOutlined,
    LoopOutlined
} from "@mui/icons-material";
import { AppDialog, useAppDialog } from "app/utils";
import { useNavigate } from "react-router-dom";
import { NAV_BREAKPOINT } from "app/components/navbar";
import { COPY } from "app/constants";

import { getLoanAssetPath, useUserLoanStatus } from "../../common";
import MakeRepayment from "./MakeRepayment";
import TopUp from "./TopUp";
import RefinanceLoan from "./RefinanceLoan";
import SellLoan from "./SellLoan";
import CollateralWithdraw from "./CollateralWithdraw";
import LoopUnwind from "./LoopUnwind";
import IncreasePrincipal from "./IncreasePrincipal";
import { ManageWhirlpool } from "./whirlpool";
import { useLoanMarketHoursAlertConfig } from "../util";
import RolloverPt from "./RolloverPt";
import { ManageLeverage } from "./payments";
import { ManageRaydium } from "./raydium";

type LoanAction = {
    label: string;
    component: ReactNode;
    icon?: ReactNode;
    hide?: boolean;
    hideDialogHeader?: boolean;
    isDrawer?: boolean;
    maxWidth?: number;
};

enum LoanActionType {
    Repay,
    Refinance,
    BorrowMore,
    TopUp,
    WithdrawCollateral,
    Unwind,
    ManagePosition,
    Rollover,
    ManageLeverage
}

type LoanActions = LoanAction[] | null;

export function useLoanActions({
    loanExpanded,
    loopPosition
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition?: LoopPositionExpanded;
}): LoanActions {
    const userLoanStatus = useUserLoanStatus(loanExpanded);
    const { isClosed: disabledMarketHours } = useLoanMarketHoursAlertConfig(loanExpanded);
    // hide on loading
    if (!loanExpanded || !userLoanStatus) return null;

    const { isBorrower, isLender } = userLoanStatus;

    const hasRolloverOptions = !!loopPosition?.loopExpanded?.loopVault.rolloverId;

    // hide, since actions are user specific
    if (!isBorrower && !isLender) return null;

    if (isLoanActive(loanExpanded) || isLoanDefaulted(loanExpanded)) {
        if (isLender) {
            const actions: LoanAction[] = [];
            if (canLenderSellLoan(loanExpanded)) {
                actions.push({ component: <SellLoan loanExpanded={loanExpanded} />, label: "Early withdraw" });
            }
            return actions;
        }

        if (isBorrower) {
            const collateral = getZcLoanCollateral(loanExpanded);
            const isDefault = isLoanDefaulted(loanExpanded);
            const isWhirlpool = isWhirlpoolMetadata(collateral?.metadata);
            const isRaydium = isRaydiumMetadata(collateral?.metadata);
            const isClmm = isWhirlpool || isRaydium;

            const isLoop = isLoopLoan(loanExpanded) && !!loopPosition;
            const repay: LoanAction = {
                icon: <PaymentOutlined />,
                label: getLoanActionLabel(LoanActionType.Repay),
                component: <MakeRepayment loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                hideDialogHeader: true,
                hide: isDefault
            };

            const isFullyRepaidLoan = isLoanFullyRepaid(loanExpanded);

            const isLoanInRefinanceGrace = isLedgerInRefinanceGrace(loanExpanded, getZcLedger(loanExpanded));

            /**
             * When an Orca position gets fully liquidated, there is no situation where a borrower can withdraw collateral
             * since we sell and close the position then repay the principal.
             * If collateral value was a little higher than principal,
             * that would just be a loss for the borrower.
             */
            const isClmmDefault = isClmm && isDefault;

            const actions: LoanAction[] = [
                ...(hasRolloverOptions
                    ? [
                          {
                              label: getLoanActionLabel(LoanActionType.Rollover),
                              icon: <LoopOutlined />,
                              component: <RolloverPt loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                              hideDialogHeader: true,
                              maxWidth: LOOP_UNWIND_MAX_WIDTH
                          }
                      ]
                    : []),
                {
                    label: getLoanActionLabel(LoanActionType.Unwind),
                    icon: <CheckOutlined />,
                    component: <LoopUnwind loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                    hide: !isLoop || isDefault,
                    hideDialogHeader: true,
                    maxWidth: LOOP_UNWIND_MAX_WIDTH
                },
                repay,
                {
                    label: getLoanActionLabel(LoanActionType.ManagePosition),
                    icon: <HealthAndSafetyOutlined />,
                    component: isWhirlpool ? (
                        <ManageWhirlpool loanExpanded={loanExpanded} />
                    ) : (
                        <ManageRaydium loanExpanded={loanExpanded} />
                    ),
                    hide: !isClmm || disabledMarketHours || isFullyRepaidLoan || isDefault,
                    hideDialogHeader: true,
                    isDrawer: true
                },
                {
                    label: getLoanActionLabel(LoanActionType.Refinance),
                    icon: <MoreTimeOutlined />,
                    component: <RefinanceLoan loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                    hide: isFullyRepaidLoan || disabledMarketHours || isDefault
                },
                {
                    label: getLoanActionLabel(LoanActionType.BorrowMore),
                    icon: <Icon type="add" />,
                    component: <IncreasePrincipal loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                    hide: isLoanInRefinanceGrace || isFullyRepaidLoan || disabledMarketHours || isDefault
                },
                {
                    label: getLoanActionLabel(LoanActionType.TopUp),
                    icon: <HealthAndSafetyOutlined />,
                    component: <TopUp loanExpanded={loanExpanded} />,
                    hide: isClmm || isFullyRepaidLoan || disabledMarketHours || isDefault
                },
                {
                    label: getLoanActionLabel(LoanActionType.WithdrawCollateral),
                    icon: <ArrowOutwardOutlined />,
                    component: <CollateralWithdraw loanExpanded={loanExpanded} />,
                    hide:
                        (!isFullyRepaidLoan && (isClmm || disabledMarketHours || isLoanInRefinanceGrace)) ||
                        isClmmDefault
                },
                {
                    label: getLoanActionLabel(LoanActionType.ManageLeverage),
                    icon: <CheckOutlined />,
                    component: <ManageLeverage loanExpanded={loanExpanded} loopPosition={loopPosition} />,
                    hide: !isLoop || !isManageLeverageEligable(loanExpanded) || isDefault,
                    hideDialogHeader: true,
                    maxWidth: LOOP_UNWIND_MAX_WIDTH
                }
            ];

            return actions.filter((a) => !a.hide);
        }
    }
    return null;
}

export function LoanActions({
    loanExpanded,
    loopPosition,
    linkToDetailCta,
    isCondensed,
    maxItems = 2
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition?: LoopPositionExpanded | undefined;
    linkToDetailCta: string | null;
    isCondensed: boolean;
    maxItems?: number;
}) {
    const maxButtons = isCondensed ? 1 : maxItems;
    const fullWidth = isCondensed ? true : false;
    const { open } = useAppDialog();
    const ctas = useLoanActions({ loanExpanded, loopPosition });

    if (!ctas?.length && !linkToDetailCta) return null;

    return (
        <Row spacing={1}>
            {ctas?.slice(0, maxButtons).map((cta, i) => (
                <Button
                    fullWidth={fullWidth}
                    sx={{ minWidth: "max-content" }}
                    key={i}
                    variant={(() => {
                        if (isCondensed) return "contained";
                        if (i === 0) return "contained";
                        return "outlined";
                    })()}
                    disabled={!loanExpanded}
                    onClick={() => {
                        if (!loanExpanded) return;
                        open(AppDialog.LoanActions, {
                            loan: loanExpanded,
                            action: cta,
                            hideHeader: cta.hideDialogHeader,
                            isDrawer: cta.isDrawer,
                            maxWidth: cta.maxWidth
                        });
                    }}
                >
                    {cta.label}
                </Button>
            ))}
            <LoanActionButtons
                buttonProps={{
                    fullWidth,
                    variant: "outlined",
                    sx: { minWidth: "max-content" }
                }}
                linkToDetailCta={linkToDetailCta}
                loan={loanExpanded}
                actions={ctas?.slice(maxButtons) ?? []}
            />
        </Row>
    );
}

export function LoanActionDialog() {
    const { getData } = useAppDialog();
    const data = getData(AppDialog.LoanActions);

    const name = abbreviateString(getLoanName(data?.loan), 40);

    return (
        <DialogWrapper maxWidth={data?.maxWidth} drawerQuery={NAV_BREAKPOINT} isDrawer={!!data?.isDrawer}>
            {(() => {
                if (data?.hideHeader) return null;
                if (data?.includeLoanHeader) {
                    return (
                        <Column spacing={0.5}>
                            <Text fontWeight="normal" variant="h3">
                                {name}
                            </Text>
                            <Text color="caption">{data?.action?.label}</Text>
                        </Column>
                    );
                }
                return <Text variant="h3">{data?.action?.label}</Text>;
            })()}

            {data?.action?.component}
        </DialogWrapper>
    );
}

export function LoanActionButtons({
    loan,
    actions,
    linkToDetailCta,
    buttonProps,
    maxItems
}: {
    loan: AbfLoanExpanded | undefined;
    actions: LoanAction[];
    linkToDetailCta: string | null;
    buttonProps?: Omit<ButtonProps, "onClick" | "disabled">;
    maxItems?: number;
}) {
    const { open } = useAppDialog();
    const navigate = useNavigate();

    const { open: openPopover, popoverProps, close } = usePopover();

    const allMenuItems: MenuItemProps[] = actions.map((a) => ({
        text: a.label,
        icon: a.icon,
        onClick: () => {
            if (!loan) return;
            open(AppDialog.LoanActions, {
                loan,
                includeLoanHeader: true,
                action: a,
                hideHeader: a.hideDialogHeader,
                isDrawer: a.isDrawer
            });
        }
    }));
    if (linkToDetailCta) {
        allMenuItems.push({
            text: linkToDetailCta,
            icon: <FeedOutlined />,
            onClick: () => {
                if (!loan) return;
                navigate(getLoanAssetPath(loan.loan));
            }
        });
    }
    const menuItems = maxItems ? allMenuItems.slice(0, maxItems) : allMenuItems;
    if (menuItems.length === 1) {
        return (
            <Button disabled={!loan} {...buttonProps} onClick={menuItems[0].onClick}>
                {menuItems[0].text}
            </Button>
        );
    }

    if (menuItems.length === 0) return null;

    return (
        <>
            <Button disabled={!loan} {...buttonProps} onClick={openPopover}>
                More <ExpandMore />
            </Button>
            <PopoverWrapper
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ horizontal: "left", vertical: -SPACING }}
                {...popoverProps}
            >
                <MenuItems close={close} items={menuItems} />
            </PopoverWrapper>
        </>
    );
}

function getLoanActionLabel(action: LoanActionType): string {
    switch (action) {
        case LoanActionType.Repay:
            return "Repay";
        case LoanActionType.Refinance:
            return "Adjust terms";
        case LoanActionType.BorrowMore:
            return "Borrow more";
        case LoanActionType.TopUp:
            return "Top-up collateral";
        case LoanActionType.WithdrawCollateral:
            return "Withdraw collateral";
        case LoanActionType.Unwind:
            return COPY.UNWIND_TERM;
        case LoanActionType.ManagePosition:
            return "Manage position";
        case LoanActionType.Rollover:
            return COPY.LOOP_ROLLOVER_TERM;
        case LoanActionType.ManageLeverage:
            return "Manage leverage";
    }
}
